import { CommonModule } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";

@Component({
  selector: 'app-make-primary-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule
  ],
  template: `
    <div class="make-primary-dialog p-3">
      <!-- Fixed Header -->
      <div class="dialog-header">
        <div class="header-content">
          <mat-icon class="primary-icon">swap_horiz</mat-icon>
          <div class="header-text">
            <h2>Make Current Host Primary</h2>
            <p>Switch primary role assignment</p>
          </div>
        </div>
      </div>

      <!-- Scrollable Content -->
      <div class="dialog-body p-2">
        <div class="info-section">
          <h3>This operation will:</h3>
          <div class="operation-list">
            <div class="operation-item">
              <mat-icon>computer</mat-icon>
              <span>Make current host (<strong>{{ data.currentHostIp }}</strong>) the new primary</span>
            </div>
            <div class="operation-item">
              <mat-icon>swap_horizontal_circle</mat-icon>
              <span>Change the previous primary host to secondary</span>
            </div>
            <div class="operation-item">
              <mat-icon>restart_alt</mat-icon>
              <span>Restart sync services on both hosts</span>
            </div>
            <div class="operation-item">
              <mat-icon>sync</mat-icon>
              <span>Reinitialize file system synchronization</span>
            </div>
          </div>
        </div>
        
        <div class="config-section" *ngIf="data.primaryHost && data.secondaryHost">
          <h3>Current Configuration:</h3>
          <div class="host-grid">
            <div class="host-row">
              <span class="host-label">Primary:</span>
              <span class="host-value">{{ data.primaryHost.hostName }} ({{ data.primaryHost.hostIp }})</span>
            </div>
            <div class="host-row">
              <span class="host-label">Secondary:</span>
              <span class="host-value">{{ data.secondaryHost.hostName }} ({{ data.secondaryHost.hostIp }})</span>
            </div>
          </div>
        </div>
        
        <div class="warning-section" *ngIf="isCurrentHostAlreadyPrimary">
          <div class="warning-box">
            <mat-icon>info</mat-icon>
            <span>Current host is already the primary host.</span>
          </div>
        </div>
        
        <div class="confirmation-section" *ngIf="!isCurrentHostAlreadyPrimary">
          <div class="input-wrapper">
            <label class="input-label">Type <strong>"SWITCH"</strong> to confirm:</label>
            <mat-form-field appearance="outline" class="confirmation-field">
              <input matInput 
                     [(ngModel)]="confirmationText" 
                     (input)="onInputChange()"
                     placeholder="Type SWITCH here..."
                     maxlength="6"
                     class="confirmation-input"
                     autocomplete="off">
            </mat-form-field>
            
            <div class="error-message" *ngIf="showError">
              <mat-icon>info</mat-icon>
              Please type "SWITCH" exactly to proceed.
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="dialog-actions">
        <button mat-stroked-button 
                class="cancel-button"
                (click)="onCancel()">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button 
                color="primary"
                class="confirm-button"
                [disabled]="!isConfirmationValid || isCurrentHostAlreadyPrimary"
                (click)="onConfirm()">
          <mat-icon>swap_horiz</mat-icon>
          {{ isCurrentHostAlreadyPrimary ? 'Already Primary' : 'Make Primary' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .make-primary-dialog {
      width: 550px;
      max-width: 90vw;
      max-height: 80vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    /* Fixed Header */
    .dialog-header {
      background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
      color: white;
      padding: 0;
      margin: -24px -24px 0 -24px;
      flex-shrink: 0;
      border-radius: 4px 4px 0 0;
    }

    .header-content {
      display: flex;
      align-items: center;
      padding: 20px 24px;
      gap: 16px;
    }

    .primary-icon {
      font-size: 28px;
      width: 28px;
      height: 28px;
      flex-shrink: 0;
      color: #ffeb3b;
    }

    .header-text {
      flex: 1;
      
      h2 {
        margin: 0 0 4px 0;
        font-size: 18px;
        font-weight: 600;
        line-height: 1.2;
      }
      
      p {
        margin: 0;
        font-size: 13px;
        opacity: 0.9;
        font-weight: 400;
      }
    }

    /* Scrollable Body */
    .dialog-body {
      flex: 1;
      overflow-y: auto;
      padding: 20px 0;
      margin: 0;
    }

    .info-section,
    .config-section,
    .warning-section,
    .confirmation-section {
      margin-bottom: 20px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }

    .info-section h3,
    .config-section h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }

    .operation-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .operation-item {
      display: flex;
      align-items: center;
      gap: 12px;
      
      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: #2196f3;
        flex-shrink: 0;
      }
      
      span {
        font-size: 13px;
        color: #555;
        line-height: 1.4;
        
        strong {
          color: #2196f3;
          font-weight: 600;
        }
      }
    }

    .host-grid {
      background: #f8f9fa;
      border-radius: 6px;
      padding: 16px;
      border: 1px solid #e9ecef;
    }

    .host-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 0;
      
      &:not(:last-child) {
        border-bottom: 1px solid #e9ecef;
        padding-bottom: 8px;
        margin-bottom: 8px;
      }
    }

    .host-label {
      font-weight: 500;
      color: #666;
      font-size: 13px;
    }

    .host-value {
      font-family: 'Courier New', monospace;
      color: #333;
      font-size: 12px;
      font-weight: 500;
    }

    .warning-box {
      display: flex;
      align-items: center;
      gap: 10px;
      background: linear-gradient(45deg, #fff3e0 0%, #fce4ec 100%);
      border: 1px solid #ff9800;
      border-radius: 6px;
      padding: 14px 16px;
      
      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #f57c00;
        flex-shrink: 0;
      }
      
      span {
        color: #ef6c00;
        font-size: 13px;
        font-weight: 500;
      }
    }

    .input-wrapper {
      .input-label {
        display: block;
        font-size: 13px;
        font-weight: 500;
        color: #333;
        margin-bottom: 12px;
        
        strong {
          color: #2196f3;
          font-weight: 600;
        }
      }
    }

    .confirmation-field {
      width: 100%;
      margin-bottom: 8px;
    }

    .confirmation-input {
      font-family: 'Courier New', monospace;
      font-weight: 700;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 1px;
      text-align: center;
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #f44336;
      font-size: 12px;
      font-weight: 500;
      
      mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
    }

    /* Fixed Footer */
    .dialog-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 0 0 0;
      border-top: 1px solid #e0e0e0;
      gap: 12px;
      flex-shrink: 0;
      margin-top: auto;
    }

    .cancel-button,
    .confirm-button {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 110px;
      height: 36px;
      font-weight: 500;
      font-size: 13px;
      
      mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
    }

    .cancel-button {
      color: #666;
      border-color: #ccc;
      
      &:hover {
        background-color: #f5f5f5;
        border-color: #999;
      }
    }

    .confirm-button {
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      &:not(:disabled):hover {
        background-color: #1976d2;
      }
    }

    /* Custom Scrollbar */
    .dialog-body::-webkit-scrollbar {
      width: 6px;
    }

    .dialog-body::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    .dialog-body::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
      
      &:hover {
        background: #a8a8a8;
      }
    }

    /* Responsive Design */
    @media (max-width: 600px) {
      .make-primary-dialog {
        width: 100%;
        max-width: 100vw;
        margin: 0;
      }
      
      .header-content {
        padding: 16px 20px;
        gap: 12px;
      }
      
      .header-text h2 {
        font-size: 16px;
      }
      
      .dialog-actions {
        flex-direction: column-reverse;
        gap: 8px;
        
        .cancel-button,
        .confirm-button {
          width: 100%;
          min-width: auto;
        }
      }
      
      .host-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
      }
    }

    /* Animation */
    .make-primary-dialog {
      animation: slideIn 0.25s ease-out;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
  `]
})
export class MakePrimaryConfirmationDialogComponent {
  confirmationText: string = '';
  showError: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<MakePrimaryConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  get isConfirmationValid(): boolean {
    return this.confirmationText === 'SWITCH';
  }

  get isCurrentHostAlreadyPrimary(): boolean {
    return this.data.primaryHost?.hostIp === this.data.currentHostIp;
  }

  onInputChange() {
    this.showError = this.confirmationText.length > 0 && !this.isConfirmationValid;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onConfirm() {
    if (this.isCurrentHostAlreadyPrimary) {
      this.dialogRef.close();
      return;
    }
    
    if (this.isConfirmationValid) {
      this.dialogRef.close('confirmed');
    } else {
      this.showError = true;
    }
  }
}